import React from 'react';
import { cn } from '../../utils/cn';

/**
 * Badge Component
 *
 * Small labels for categorization, status, and metadata.
 * Uses standard Tailwind classes for maximum compatibility.
 */

const variants = {
  // Solid variants
  default: 'bg-gray-100 text-gray-600',
  primary: 'bg-blue-100 text-blue-700',
  accent: 'bg-amber-100 text-amber-700',
  success: 'bg-green-100 text-green-600',
  warning: 'bg-amber-100 text-amber-600',
  error: 'bg-red-100 text-red-600',

  // Outline variants
  'outline-default': 'bg-transparent border border-gray-200 text-gray-600',
  'outline-primary': 'bg-transparent border border-blue-300 text-blue-600',

  // Special - for beats and topics
  beat: 'bg-blue-50 text-blue-600 border border-blue-200',
  topic: 'bg-amber-50 text-amber-600 border border-amber-200',
};

const sizes = {
  xs: 'px-1.5 py-0.5 text-[10px]',
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-2.5 py-1 text-xs',
  lg: 'px-3 py-1.5 text-sm',
};

export function Badge({
  children,
  variant = 'default',
  size = 'sm',
  icon: Icon,
  removable,
  onRemove,
  className,
  ...props
}) {
  return (
    <span
      className={cn(
        // Base styles
        'inline-flex items-center gap-1',
        'font-medium',
        'rounded-md',
        'whitespace-nowrap',
        'transition-all duration-150',

        // Variant
        variants[variant],

        // Size
        sizes[size],

        // Interactive styles (when removable)
        removable && 'pr-1',

        className
      )}
      {...props}
    >
      {Icon && <Icon className="w-3 h-3 opacity-70" />}
      {children}
      {removable && (
        <button
          type="button"
          onClick={onRemove}
          className="ml-0.5 p-0.5 rounded-full hover:bg-black/10 transition-colors duration-150 focus:outline-none focus:ring-1 focus:ring-current"
          aria-label="Remove"
        >
          <svg
            className="w-3 h-3"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2.5}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </span>
  );
}

/**
 * Tag Component - A clickable badge variant
 */
export function Tag({
  children,
  variant = 'default',
  size = 'sm',
  icon: Icon,
  selected,
  onClick,
  className,
  ...props
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        // Base styles
        'inline-flex items-center gap-1',
        'font-medium',
        'rounded-md',
        'whitespace-nowrap',
        'transition-all duration-150',
        'cursor-pointer',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1',

        // Size
        sizes[size],

        // Selected state
        selected
          ? 'bg-blue-600 text-white shadow-sm'
          : [variants[variant], 'hover:bg-gray-200'],

        className
      )}
      {...props}
    >
      {Icon && <Icon className="w-3 h-3 opacity-70" />}
      {children}
    </button>
  );
}

/**
 * Badge Group - For displaying multiple badges with consistent spacing
 */
export function BadgeGroup({ children, className, ...props }) {
  return (
    <div className={cn('flex flex-wrap gap-1.5', className)} {...props}>
      {children}
    </div>
  );
}

/**
 * Status Badge - Dot + text for status indicators
 */
export function StatusBadge({
  status = 'default',
  children,
  size = 'sm',
  className,
  ...props
}) {
  const statusColors = {
    default: 'bg-gray-400',
    active: 'bg-green-500',
    pending: 'bg-amber-500',
    error: 'bg-red-500',
    primary: 'bg-blue-500',
  };

  const dotSizes = {
    xs: 'w-1.5 h-1.5',
    sm: 'w-2 h-2',
    md: 'w-2 h-2',
    lg: 'w-2.5 h-2.5',
  };

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5',
        'text-gray-600',
        sizes[size],
        'px-0', // Override padding for status badge
        className
      )}
      {...props}
    >
      <span className={cn('rounded-full', statusColors[status], dotSizes[size])} />
      {children}
    </span>
  );
}

export default Badge;
